import { useEffect, useState } from 'react'
import {
  Plus,
  Trash2,
  Check,
  Save,
  Package,
  Loader2,
} from 'lucide-react'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../lib/shadcn/card'

import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'

import {
  RadioGroup,
  RadioGroupItem,
} from '../../lib/shadcn/radio-group'

import { cn } from '../../lib/shadcn/utils'
import { toast } from '../../lib/shadcn/sonner'

import {
  useShippingSettings,
  ENVIA_CARRIER_OPTIONS,
  createParcelPresetId,
  type ShippingSettings as ShippingSettingsType,
  type ShipFromAddress,
  type ParcelPreset,
  type EnviaCarrier,
} from '../../utils/shippingSettings'

import ShippingFeeSettings from './ShippingFeeSettings'

// ─── Parcel draft ──────────────────────────────────────────────────────────

interface PresetDraft {
  name: string
  length: string
  width: string
  height: string
}

const EMPTY_DRAFT: PresetDraft = {
  name: '',
  length: '',
  width: '',
  height: '',
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function ShippingSettings() {
  const {
    settings,
    loaded,
    loading,
    save,
  } = useShippingSettings()

  const [draft, setDraft] =
    useState<ShippingSettingsType | null>(null)

  const [presetDraft, setPresetDraft] =
    useState<PresetDraft>(EMPTY_DRAFT)

  // Populate the editable draft once the DB settings load.
  useEffect(() => {
    if (loaded) {
      setDraft(settings)
    }
  }, [loaded, settings])

  // ─── Loading ───────────────────────────────────────────────────────────

  if (!loaded) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading shipping settings...
      </div>
    )
  }

  if (!draft) {
    return (
      <div className="text-sm text-destructive">
        Unable to load shipping settings.
      </div>
    )
  }

  // ─── Helpers ───────────────────────────────────────────────────────────

  const updateAddress = (
    key: keyof ShipFromAddress,
    value: string
  ) => {
    setDraft(prev => {
      if (!prev) return prev

      return {
        ...prev,
        shipFromAddress: {
          ...prev.shipFromAddress,
          [key]: value,
        },
      }
    })
  }

 const toggleCarrier = async (
  carrier: EnviaCarrier,
  checked: boolean
) => {
  let nextCarriers: EnviaCarrier[]

  if (checked) {
    nextCarriers = Array.from(
      new Set([
        ...draft.enviaCarriers,
        carrier,
      ])
    )
  } else {
    if (draft.enviaCarriers.length <= 1) {
      toast.error(
        'At least one Envia carrier must be selected'
      )
      return
    }

    nextCarriers =
      draft.enviaCarriers.filter(
        c => c !== carrier
      )
  }

  const nextSettings: ShippingSettingsType = {
    ...draft,
    enviaCarriers: nextCarriers,
  }

  try {
    await save(nextSettings)

    setDraft(nextSettings)

    toast.success(
      'Envia carriers updated'
    )
  } catch (e) {
    toast.error(
      e instanceof Error
        ? e.message
        : 'Failed to update Envia carriers'
    )
  }
}

  const handleAddPreset = async () => {
  const length = Number(presetDraft.length)
  const width = Number(presetDraft.width)
  const height = Number(presetDraft.height)

  if (!presetDraft.name.trim()) {
    toast.error('Give the preset a name')
    return
  }

  if (
    ![length, width, height].every(
      n => Number.isFinite(n) && n > 0
    )
  ) {
    toast.error(
      'Length, width, and height must be positive numbers'
    )
    return
  }

  const preset: ParcelPreset = {
    id: createParcelPresetId(),
    name: presetDraft.name.trim(),
    length,
    width,
    height,
  }

  const nextSettings: ShippingSettingsType = {
    ...draft,
    parcelPresets: [
      ...draft.parcelPresets,
      preset,
    ],
    defaultParcelPresetId:
      draft.parcelPresets.length === 0
        ? preset.id
        : draft.defaultParcelPresetId,
  }

  try {
    await save(nextSettings)

    setDraft(nextSettings)
    setPresetDraft(EMPTY_DRAFT)

    toast.success('Parcel preset added')
  } catch (e) {
    toast.error(
      e instanceof Error
        ? e.message
        : 'Failed to save parcel preset'
    )
  }
}

  const handleRemovePreset = async (id: string) => {
  if (draft.parcelPresets.length <= 1) {
    toast.error('At least one parcel preset is required')
    return
  }

  const presets = draft.parcelPresets.filter(
    preset => preset.id !== id
  )

  let defaultId = draft.defaultParcelPresetId

  if (defaultId === id) {
    defaultId = presets[0]?.id ?? ''
  }

  const nextSettings: ShippingSettingsType = {
    ...draft,
    parcelPresets: presets,
    defaultParcelPresetId: defaultId,
  }

  try {
    await save(nextSettings)

    setDraft(nextSettings)

    toast.success('Parcel preset deleted')
  } catch (e) {
    toast.error(
      e instanceof Error
        ? e.message
        : 'Failed to delete parcel preset'
    )
  }
}

  const handleDefaultPresetChange = async (id: string) => {
  if (id === draft.defaultParcelPresetId) {
    return
  }

  const exists = draft.parcelPresets.some(
    preset => preset.id === id
  )

  if (!exists) {
    return
  }

  const nextSettings: ShippingSettingsType = {
    ...draft,
    defaultParcelPresetId: id,
  }

  try {
    await save(nextSettings)

    setDraft(nextSettings)

    toast.success('Default parcel preset updated')
  } catch (e) {
    toast.error(
      e instanceof Error
        ? e.message
        : 'Failed to update default parcel preset'
    )
  }
}

  const handleShippingProviderChange = async (
  provider: 'shippo' | 'envia'
) => {
  if (provider === draft.shippingProvider) {
    return
  }

  const nextSettings: ShippingSettingsType = {
    ...draft,
    shippingProvider: provider,
  }

  try {
    await save(nextSettings)

    setDraft(nextSettings)

    toast.success(
      `Shipping provider changed to ${
        provider === 'envia' ? 'Envia Shipping' : 'Shippo'
      }`
    )
  } catch (e) {
    toast.error(
      e instanceof Error
        ? e.message
        : 'Failed to update shipping provider'
    )
  }
}

  const handleSaveAddress = async () => {
  if (
    !draft.shipFromAddress.street1.trim() ||
    !draft.shipFromAddress.city.trim() ||
    !draft.shipFromAddress.state.trim() ||
    !draft.shipFromAddress.zip.trim()
  ) {
    toast.error(
      'Street, city, state, and ZIP are required'
    )
    return
  }

  const nextSettings: ShippingSettingsType = {
    ...settings,
    shipFromAddress: draft.shipFromAddress,
  }

  try {
    await save(nextSettings)

    setDraft(nextSettings)

    toast.success('Ship-from address saved')
  } catch (e) {
    toast.error(
      e instanceof Error
        ? e.message
        : 'Failed to save ship-from address'
    )
  }
}



  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Shipping Provider */}

      <Card>
        <CardHeader>
          <CardTitle>
            Shipping Provider
          </CardTitle>

          <CardDescription>
            Choose the service used for rates,
            labels, and tracking for new shipments.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">

          <RadioGroup
  value={draft.shippingProvider}
  onValueChange={value =>
    void handleShippingProviderChange(
      value as 'shippo' | 'envia'
    )
  }
  disabled={loading}
  className="gap-3"
>
            <Label
              htmlFor="shipping-provider-shippo"
              className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer"
            >
              <RadioGroupItem
                value="shippo"
                id="shipping-provider-shippo"
                className="mt-1"
              />

              <div>
                <div className="font-medium">
                  Shippo
                </div>

                <p className="text-sm text-muted-foreground font-normal">
                  Use the existing Shippo
                  integration.
                </p>
              </div>
            </Label>

            <Label
              htmlFor="shipping-provider-envia"
              className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer"
            >
              <RadioGroupItem
                value="envia"
                id="shipping-provider-envia"
                className="mt-1"
              />

              <div>
                <div className="font-medium">
                  Envia Shipping
                </div>

                <p className="text-sm text-muted-foreground font-normal">
                  Use Envia for multi-carrier
                  rates, labels, and tracking.
                </p>
              </div>
            </Label>
          </RadioGroup>

          {draft.shippingProvider ===
            'envia' && (
            <div className="rounded-lg border p-4 space-y-3">

              <div>
                <div className="font-medium">
                  Envia Carriers
                </div>

                <p className="text-sm text-muted-foreground">
                  Select the carriers to include
                  when getting rates.
                </p>
              </div>

              <div className="space-y-2">

                {ENVIA_CARRIER_OPTIONS.map(
                  carrier => (
                    <Label
                      key={carrier.id}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <input
  type="checkbox"
  checked={draft.enviaCarriers.includes(
    carrier.id
  )}
  disabled={loading}
  onChange={e =>
    void toggleCarrier(
      carrier.id,
      e.target.checked
    )
  }
  className="h-4 w-4"
/>

                      <span>
                        {carrier.name}
                      </span>
                    </Label>
                  )
                )}

              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Ship From Address */}

      <Card>
        <CardHeader>
          <CardTitle>
            Ship From Address
          </CardTitle>

          <CardDescription>
            The origin address used when
            generating shipping labels.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <AddressField
              fieldKey="name"
              label="Name"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

            <AddressField
              fieldKey="company"
              label="Company"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <AddressField
              fieldKey="street1"
              label="Street Address"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

            <AddressField
              fieldKey="street2"
              label="Street Address 2"
              placeholder="Suite, unit, etc. (optional)"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

            <AddressField
              fieldKey="city"
              label="City"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

            <AddressField
              fieldKey="state"
              label="State"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

            <AddressField
              fieldKey="zip"
              label="Zip"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <AddressField
              fieldKey="phone"
              label="Phone"
              placeholder="Optional"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

            <AddressField
              fieldKey="email"
              label="Email"
              placeholder="Optional"
              address={
                draft.shipFromAddress
              }
              update={updateAddress}
            />

          </div>
<div className="flex justify-end pt-4">
  <Button
    type="button"
    onClick={() => void handleSaveAddress()}
    disabled={
      loading ||
      JSON.stringify(draft.shipFromAddress) ===
        JSON.stringify(settings.shipFromAddress)
    }
  >
    {loading ? (
      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
    ) : (
      <Save className="w-4 h-4 mr-2" />
    )}

    {loading ? 'Saving...' : 'Save Address'}
  </Button>
</div>
        </CardContent>
      </Card>

      {/* Parcel Dimensions */}

      <Card>
        <CardHeader>
          <CardTitle>
            Parcel Dimensions
          </CardTitle>

          <CardDescription>
            Set up box sizes to choose from when
            generating a shipping label. Mark one
            as the default.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">

          <RadioGroup
  value={draft.defaultParcelPresetId}
  onValueChange={id =>
    void handleDefaultPresetChange(id)
  }
  disabled={loading}
  className="gap-3"
>

            {draft.parcelPresets.map(
              preset => {
                const selected =
                  draft.defaultParcelPresetId ===
                  preset.id

                return (
                  <Label
                    key={preset.id}
                    htmlFor={`parcel-${preset.id}`}
                    className={cn(
                      'flex items-start gap-4 rounded-lg border p-4 cursor-pointer transition-colors',
                      selected
                        ? 'border-primary bg-accent'
                        : 'border-border hover:bg-accent/50'
                    )}
                  >
                    <RadioGroupItem
                      value={preset.id}
                      id={`parcel-${preset.id}`}
                      className="mt-1"
                    />

                    <Package className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />

                    <div className="flex-1 space-y-1">

                      <div className="flex items-center gap-2">

                        <span className="font-medium text-sm">
                          {preset.name}
                        </span>

                        {selected && (
                          <Check className="w-4 h-4 text-primary" />
                        )}

                      </div>

                      <p className="text-sm text-muted-foreground font-normal">
                        {preset.length} x{' '}
                        {preset.width} x{' '}
                        {preset.height} in 
                      </p>

                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={loading}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                      aria-label={`Remove ${preset.name}`}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                    
                        void handleRemovePreset(preset.id)
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>

                  </Label>
                )
              }
            )}

          </RadioGroup>

          <div className="border-t pt-4 space-y-3">

            <Label>
              Add a Preset
            </Label>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">

              <Input
                placeholder="Name (e.g. Small Box)"
                value={presetDraft.name}
                onChange={e =>
                  setPresetDraft(prev => ({
                    ...prev,
                    name:
                      e.target.value,
                  }))
                }
                className="sm:col-span-2"
              />

              <Input
                type="number"
                min={0}
                placeholder="Length (in)"
                value={presetDraft.length}
                onChange={e =>
                  setPresetDraft(prev => ({
                    ...prev,
                    length:
                      e.target.value,
                  }))
                }
              />

              <Input
                type="number"
                min={0}
                placeholder="Width (in)"
                value={presetDraft.width}
                onChange={e =>
                  setPresetDraft(prev => ({
                    ...prev,
                    width:
                      e.target.value,
                  }))
                }
              />

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">

              <Input
                type="number"
                min={0}
                placeholder="Height (in)"
                value={presetDraft.height}
                onChange={e =>
                  setPresetDraft(prev => ({
                    ...prev,
                    height:
                      e.target.value,
                  }))
                }
                className="sm:col-start-3"
              />

              <Button
  type="button"
  onClick={() => void handleAddPreset()}
  disabled={loading}
  className="gap-1.5"
>
  {loading ? (
    <Loader2 className="w-4 h-4 animate-spin" />
  ) : (
    <Plus className="w-4 h-4" />
  )}

  {loading ? 'Saving...' : 'Add Preset'}
</Button>

            </div>

          </div>

        </CardContent>
      </Card>

      <ShippingFeeSettings />

    </div>
  )
}

// ─── Address field ─────────────────────────────────────────────────────────

function AddressField({ 
  fieldKey,
  label,
  placeholder,
  address,
  update,
}: {
  fieldKey: keyof ShipFromAddress 
  label: string
  placeholder?: string
  address: ShipFromAddress
  update: (
    key: keyof ShipFromAddress,
    value: string
  ) => void
}) {
  return (
    <div className="space-y-1.5">

      <Label
        htmlFor={`ship-from-${fieldKey}`}
      >
        {label}
      </Label>

      <Input
        id={`ship-from-${fieldKey}`}
        value={address[fieldKey]}
        placeholder={placeholder}
        onChange={e =>
          update(
            fieldKey,
            e.target.value
          )
        }
      />

    </div>
  )
}